import React, {useState, useEffect} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Typography,
  Divider,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import {Close as CloseIcon} from "@mui/icons-material";
import {useSessionsRealtime, useTitansByDM} from "../../hooks/useFirestore";
import {useAuth} from "../../hooks/useAuth";

import {FirestoreSession} from "../../services/firebase/firestore";
import {SessionService} from "../../services/api/sessionService";
import {useNotifications} from "../../components/NotificationSystem";
import "../../styles/env.css";
import ConfirmDeleteDialog from "../../components/ConfirmDeleteDialog";
import CharacterSheetViewer from "../../components/Character Sheet/CharacterSheetViewer";
import {
  getSessionPlayerLogs,
  SessionPlayerLog,
} from "../../services/api/characterSheetService";
import {formatLoggedAtDate, convertToDate, isSessionDone} from "../../utils/dateUtils";
import {Share2, UserPlus2} from "lucide-react";
import RichTextEditor from "../../components/RichText/RichTextEditor";
import RichTextRenderer from "../../components/RichText/RichTextRenderer";
import {claimDMTokenFunction} from "../../services/firebase/functions";
import DMAddPlayer from "../../components/DMAddPlayer";

interface PlayerLog {
  playerId: string;
  character: string;
  keyEvents: string;
  items: {name: string; quantity: number; description: string}[];
}

// States
type PageState = "creating" | "editing" | "filled" | "adding-notes";

const SessionManagement: React.FC = () => {
  const {user, isDM, isAdmin} = useAuth(); // Access user roles for DM functionality
  const {
    data: firestoreSessions,
    loading: sessionsLoading,
    error: sessionsError,
  } = useSessionsRealtime();
  const {
    data: firestoreTitans,
    loading: titansLoading,
    error: titansError,
  } = useTitansByDM(user?.uid || "");
  const {addNotification} = useNotifications();
  const location = useLocation();
  const navigate = useNavigate();

  const [pageState, setPageState] = useState<PageState>("filled");

  // Check if we should automatically open the create or edit session form
  useEffect(() => {
    if (location.state?.openCreateForm) {
      setPageState("creating");

      // Pre-populate form with date/time if provided
      if (location.state?.prefilledDate || location.state?.prefilledTime) {
        setForm((prev) => ({
          ...prev,
          date: location.state?.prefilledDate || prev.date,
          time: location.state?.prefilledTime || prev.time,
        }));
      }
    } else if (location.state?.openEditForm && location.state?.editSession) {
      // Handle edit session from calendar modal
      const session = location.state.editSession;

      // Security check: Only allow editing if user is admin OR the DM of this session
      if (!isAdmin && session.dmId !== user?.uid) {
        addNotification({
          type: "error",
          title: "Access Denied",
          message: "You can only edit sessions that you are the DM of.",
        });
        // Clear the location state to prevent further attempts
        navigate("/sessionmanagement", {replace: true});
        return;
      }

      // Use the same date conversion utility used throughout the app
      const sessionDate = convertToDate(session.date);

      // Validate the date before using it
      if (isNaN(sessionDate.getTime())) {
        console.error(
          "Invalid session date received from calendar:",
          session.date
        );
        // Don't proceed with invalid date, show error or use current date
        return;
      }

      const levelRangeMatch = session.levelRange?.match(/Level (\d+)-(\d+)/);
      let minLevel = 1,
        maxLevel = 20;
      if (levelRangeMatch) {
        minLevel = parseInt(levelRangeMatch[1], 10);
        maxLevel = parseInt(levelRangeMatch[2], 10);
      }

      setForm({
        title: session.name || "",
        description: session.description || "",
        titan: session.titanId || "",
        customWorldName: session.customWorldName || "",
        date: sessionDate.toISOString().split("T")[0],
        time: sessionDate.toTimeString().split(" ")[0].substring(0, 5),
        duration: session.duration || "",
        location: session.location || "",
        maxPlayers: session.maxPlayers || 6,
        minLevel,
        maxLevel,
      });
      // Set display state based on whether it's a custom world or titan
      if (session.customWorldName && !session.titanId) {
        setSelectedTitanDisplay("other-worlds");
      } else {
        setSelectedTitanDisplay(session.titanId || "");
      }
      setSelectedSession(session);
      setPageState("editing");
    }
  }, [location.state]);
  const [form, setForm] = useState({
    title: "",
    description: "",
    titan: "",
    customWorldName: "",
    date: "",
    time: "18:00",
    duration: "",
    location: "",
    maxPlayers: 6,
    minLevel: 1,
    maxLevel: 20,
  });
  const [selectedTitanDisplay, setSelectedTitanDisplay] = useState("");
  const [selectedSession, setSelectedSession] =
    useState<FirestoreSession | null>(null);
  const [dmNotes, setDmNotes] = useState("");
  const [playerLogs, setPlayerLogs] = useState<PlayerLog[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDeleteSessionId, setPendingDeleteSessionId] = useState<
    string | null
  >(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [operationError, setOperationError] = useState<string | null>(null);
  const [showCharacterSheets, setShowCharacterSheets] = useState(false);
  const [selectedSessionForSheets, setSelectedSessionForSheets] =
    useState<FirestoreSession | null>(null);
  const [showPlayerLogs, setShowPlayerLogs] = useState(false);
  const [selectedSessionForLogs, setSelectedSessionForLogs] =
    useState<FirestoreSession | null>(null);
  const [sessionPlayerLogs, setSessionPlayerLogs] = useState<
    SessionPlayerLog[]
  >([]);
  const [claimingTokens, setClaimingTokens] = useState<Set<string>>(new Set());
  const [claimedTokens, setClaimedTokens] = useState<Set<string>>(new Set());
  const [copiedOpen, setCopiedOpen] = useState(false);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [sessionForAdd, setSessionForAdd] = useState<FirestoreSession | null>(
    null
  );

  // Reset form to default values
  const resetForm = () => {
    setForm({
      title: "",
      description: "",
      titan: "",
      customWorldName: "",
      date: "",
      time: "18:00",
      duration: "",
      location: "",
      maxPlayers: 6,
      minLevel: 1,
      maxLevel: 20,
    });
    setSelectedTitanDisplay("");
    setSelectedSession(null);
  };

  const handleCopyCalendarLink = (sessionId: string | undefined) => {
    if (!sessionId) return;
    const url = `${window.location.origin}/calendar?sessionId=${sessionId}`;
    navigator.clipboard.writeText(url).then(() => setCopiedOpen(true));
  };

  // Filter sessions by current DM
  const sessions = firestoreSessions.filter(
    (session) => session.dmId === user?.uid
  );

  // Handlers
  const handleCreateSession = async () => {
    if (
      !form.title.trim() ||
      !form.description.trim() ||
      !form.date ||
      !form.location
    ) {
      addNotification({
        type: "error",
        title: "Validation Error",
        message: "Title, description, date, and location are required fields.",
      });
      return;
    }

    // If "Other Worlds" is selected, custom world name is required
    if (form.titan === "other-worlds" && !form.customWorldName.trim()) {
      addNotification({
        type: "error",
        title: "Validation Error",
        message: "Please provide a world name when selecting 'Other Worlds'.",
      });
      return;
    }

    // Validate that session date is not in the past
    const sessionDate = new Date(`${form.date}T${form.time || "00:00"}`);
    const now = new Date();

    // Set both dates to start of day for comparison to allow sessions on the current day
    const sessionDateStart = new Date(
      sessionDate.getFullYear(),
      sessionDate.getMonth(),
      sessionDate.getDate()
    );
    const nowStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    if (sessionDateStart < nowStart) {
      addNotification({
        type: "error",
        title: "Invalid Date",
        message:
          "Sessions cannot be created for past dates. Please select today or a future date.",
      });
      return;
    }

    setIsSubmitting(true);
    setOperationError(null);

    try {
      await SessionService.createSession({
        name: form.title,
        description: form.description,
        titanId: form.titan === "other-worlds" ? "" : form.titan,
        customWorldName:
          form.titan === "other-worlds" ? form.customWorldName : undefined,
        date: sessionDate,
        location: form.location,
        duration: form.duration,
        maxPlayers: form.maxPlayers,
        dmId: user?.uid || "",
        levelRange: `Level ${form.minLevel}-${form.maxLevel}`,
      });

      addNotification({
        type: "success",
        title: "Session Created",
        message: `Successfully created "${form.title}"`,
      });

      resetForm();
      setPageState("filled");
    } catch (error: any) {
      // Error creating session - handled by notification system
      const errorMessage =
        error.message || "Failed to create session. Please try again.";
      setOperationError(errorMessage);
      addNotification({
        type: "error",
        title: "Create Failed",
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSession = (id: string) => {
    setPendingDeleteSessionId(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (pendingDeleteSessionId) {
      try {
        await SessionService.deleteSession({
          sessionId: pendingDeleteSessionId,
        });
        addNotification({
          type: "success",
          title: "Session Deleted",
          message: "Session has been successfully deleted.",
        });
        setPendingDeleteSessionId(null);
      } catch (error: any) {
        // Error deleting session - handled by notification system
        const errorMessage =
          error.message || "Failed to delete session. Please try again.";
        addNotification({
          type: "error",
          title: "Delete Failed",
          message: errorMessage,
        });
      }
    }
    setDeleteDialogOpen(false);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setPendingDeleteSessionId(null);
  };

  const handleViewCharacterSheets = (session: FirestoreSession) => {
    setSelectedSessionForSheets(session);
    setShowCharacterSheets(true);
  };

  const handleCloseCharacterSheets = () => {
    setShowCharacterSheets(false);
    setSelectedSessionForSheets(null);
  };

  const handleViewPlayerLogs = async (session: FirestoreSession) => {
    setSelectedSessionForLogs(session);
    setShowPlayerLogs(true);

    try {
      const response = await getSessionPlayerLogs(session.id!);
      setSessionPlayerLogs(response.playerLogs);
    } catch (error: any) {
      // Error loading player logs - handled by notification system
      addNotification({
        type: "error",
        title: "Failed to Load Logs",
        message: error.message || "Failed to load player logs",
      });
    }
  };

  const handleClosePlayerLogs = () => {
    setShowPlayerLogs(false);
    setSelectedSessionForLogs(null);
    setSessionPlayerLogs([]);
  };

  const handleAddNotes = (session: FirestoreSession) => {
    setSelectedSession(session);
    setDmNotes(session.sessionLog?.events || "");
    setPlayerLogs(
      session.sessionLog
        ? [
            {
              playerId: "player-1", // This would need to be mapped from actual player data
              character: "Character Name", // This would need to be mapped from character data
              keyEvents: session.sessionLog.events,
              items: session.sessionLog.items.map((item) => ({
                name: item.name,
                quantity: item.amount,
                description: item.description,
              })),
            },
          ]
        : []
    );
    setPageState("adding-notes");
  };

  const handleSaveNotes = async () => {
    if (!selectedSession) return;

    try {
      await SessionService.updateSession({
        sessionId: selectedSession.id!,
        sessionLog: {
          events: dmNotes,
          items: playerLogs
            .flatMap((log) => log.items)
            .map((item) => ({
              name: item.name,
              type: "Item",
              amount: item.quantity,
              description: item.description,
            })),
        },
      });

      const isEditing = selectedSession.sessionLog;
      addNotification({
        type: "success",
        title: isEditing ? "Notes Updated" : "Notes Saved",
        message: isEditing
          ? "Session notes have been updated successfully."
          : "Session notes have been saved successfully.",
      });
      setSelectedSession(null);
      setPageState("filled");
    } catch (error: any) {
      // Error saving notes - handled by notification system
      const errorMessage =
        error.message || "Failed to save notes. Please try again.";
      addNotification({
        type: "error",
        title: "Save Failed",
        message: errorMessage,
      });
    }
  };

  const handleEditSession = (session: FirestoreSession) => {
    const sessionDate = new Date(session.date.toDate());
    const levelRangeMatch = session.levelRange.match(/Level (\d+)-(\d+)/);
    let minLevel = 1,
      maxLevel = 20;
    if (levelRangeMatch) {
      minLevel = parseInt(levelRangeMatch[1], 10);
      maxLevel = parseInt(levelRangeMatch[2], 10);
    }
    setForm({
      title: session.name,
      description: session.description,
      titan: session.titanId,
      customWorldName: "",
      date: sessionDate.toISOString().split("T")[0],
      time: sessionDate.toTimeString().split(" ")[0].substring(0, 5),
      duration: session.duration || "",
      location: session.location,
      maxPlayers: session.maxPlayers,
      minLevel,
      maxLevel,
    });
    setSelectedSession(session);
    setPageState("editing");
  };

  const handleSaveEditSession = async () => {
    if (!selectedSession) return;

    if (
      !form.title.trim() ||
      !form.description.trim() ||
      !form.date ||
      !form.location
    ) {
      addNotification({
        type: "error",
        title: "Validation Error",
        message: "Title, description, date, and location are required fields.",
      });
      return;
    }

    // If "Other Worlds" is selected, custom world name is required
    if (form.titan === "other-worlds" && !form.customWorldName.trim()) {
      addNotification({
        type: "error",
        title: "Validation Error",
        message: "Please provide a world name when selecting 'Other Worlds'.",
      });
      return;
    }

    setIsSubmitting(true);
    setOperationError(null);

    try {
      const sessionDate = new Date(`${form.date}T${form.time || "00:00"}`);

      await SessionService.updateSession({
        sessionId: selectedSession.id!,
        name: form.title,
        description: form.description,
        titanId: form.titan === "other-worlds" ? "" : form.titan,
        date: sessionDate,
        location: form.location,
        duration: form.duration,
        maxPlayers: form.maxPlayers,
        levelRange: `Level ${form.minLevel}-${form.maxLevel}`,
      });

      addNotification({
        type: "success",
        title: "Session Updated",
        message: `Successfully updated "${form.title}"`,
      });

      resetForm();
      setPageState("filled");
    } catch (error: any) {
      // Error updating session - handled by notification system
      const errorMessage =
        error.message || "Failed to update session. Please try again.";
      setOperationError(errorMessage);
      addNotification({
        type: "error",
        title: "Update Failed",
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // DM Token handlers
  const handleClaimDMToken = async (sessionId: string) => {
    if (!sessionId || claimingTokens.has(sessionId)) return;

    setClaimingTokens((prev) => new Set(prev).add(sessionId));

    try {
      const result = await claimDMTokenFunction({sessionId});

      if ((result.data as any)?.success) {
        setClaimedTokens((prev) => new Set(prev).add(sessionId));
        addNotification({
          type: "success",
          title: "DM Token Claimed!",
          message: `You've earned a DM token! Total tokens: ${(result.data as any).totalTokens}`,
        });
      }
    } catch (error: any) {
      console.error("Error claiming DM token:", error);
      let errorMessage = "Failed to claim DM token";

      if (error?.details?.message) {
        errorMessage = error.details.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      addNotification({
        type: "error",
        title: "Claim Failed",
        message: errorMessage,
      });
    } finally {
      setClaimingTokens((prev) => {
        const newSet = new Set(prev);
        newSet.delete(sessionId);
        return newSet;
      });
    }
  };

  // Check if DM token can be claimed for a session
  const canClaimDMToken = (session: FirestoreSession): boolean => {
    if (!session.id || claimedTokens.has(session.id)) return false;

    const isCompleted = session.status === "completed" || isSessionDone(session.date);

    return isCompleted && session.dmId === user?.uid;
  };

  // Load existing DM token claims on component mount
  useEffect(() => {
    const loadExistingClaims = async () => {
      if (!user?.uid || (!isDM() && !isAdmin())) return;

      try {
        const result = await SessionService.getDMTokenClaims();
        if (result.success) {
          const claimedSessionIds = new Set(
            result.claims.map((claim) => claim.sessionId)
          );
          setClaimedTokens(claimedSessionIds);
        }
      } catch (error) {
        console.error("Failed to load existing DM token claims:", error);
      }
    };

    loadExistingClaims();
  }, [user?.uid, isDM, isAdmin]);

  // Show loading state
  if (sessionsLoading || titansLoading) {
    return (
      <div className="env-page-container">
        <h2 className="env-page-title">Session Management</h2>
        <div className="env-session-loading-center">Loading sessions...</div>
      </div>
    );
  }

  // Show error state
  if (sessionsError || titansError) {
    return (
      <div className="env-page-container">
        <h2 className="env-page-title">Session Management</h2>
        <div className="env-session-error-center">
          Error loading data: {sessionsError || titansError}
        </div>
      </div>
    );
  }

  // UI States
  if (pageState === "creating" || pageState === "editing") {
    return (
      <div className="env-page-container">
        <h2 className="env-page-title">Session Management</h2>
        <section className="env-section">
          <div className="env-section-header">
            <button
              className="btn btn-tertiary"
              onClick={() => setPageState("filled")}
            >
              + Create new session
            </button>
          </div>
          <div className="env-card-medium">
            <div className="env-card-title env-session-edit-header">
              {pageState === "editing" && (
                <button
                  onClick={() => {
                    resetForm();
                    setPageState("filled");
                  }}
                  className="btn btn-secondary btn-small"
                  aria-label="Back"
                >
                  Back
                </button>
              )}
              {pageState === "editing" ? "EDIT SESSION" : "CREATE SESSION"}
            </div>
            {operationError && (
              <div className="env-session-operation-error">
                {operationError}
              </div>
            )}
            <div className="env-form-group">
              <label htmlFor="session-title">Title</label>
              <input
                id="session-title"
                className="env-input"
                placeholder="Session title"
                value={form.title}
                onChange={(e) => setForm({...form, title: e.target.value})}
              />
            </div>
            <div className="env-form-group">
              <label htmlFor="session-description">Description</label>
              <RichTextEditor
                value={form.description}
                onChange={(description) => setForm({...form, description})}
                placeholder="Describe the session, what players can expect, any special requirements..."
                minHeight="150px"
                maxHeight="300px"
              />
            </div>
            <div className="env-form-group">
              <label htmlFor="session-titan">Titan or Setting</label>
              <select
                id="session-titan"
                className="env-input"
                value={selectedTitanDisplay}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.startsWith("static:")) {
                    const worldName = value.slice(7);
                    setForm({...form, titan: "other-worlds", customWorldName: worldName});
                    setSelectedTitanDisplay("other-worlds");
                  } else if (value === "other-worlds") {
                    setForm({...form, titan: value});
                    setSelectedTitanDisplay(value);
                  } else if (value === "") {
                    setForm({...form, titan: "", customWorldName: ""});
                    setSelectedTitanDisplay("");
                  } else {
                    setForm({...form, titan: value, customWorldName: ""});
                    setSelectedTitanDisplay(value);
                  }
                }}
              >
                <option value="">No specific world</option>
                {firestoreTitans.map((titan) => (
                  <option key={titan.id} value={titan.id}>
                    {titan.name}
                  </option>
                ))}
                <option value="static:Forgotten Realms">Forgotten Realms</option>
                <option value="static:Ravenloft">Ravenloft</option>
                <option value="static:Eberron">Eberron</option>
                <option value="static:Planescape">Planescape</option>
                <option value="static:Dragonlance">Dragonlance</option>
                <option value="other-worlds">Other Worlds</option>
              </select>
            </div>
            {form.titan === "other-worlds" && (
              <div className="env-form-group">
                <label htmlFor="session-custom-world">
                  World Name (Optional)
                </label>
                <input
                  id="session-custom-world"
                  className="env-input"
                  placeholder="Enter custom world name"
                  value={form.customWorldName}
                  onChange={(e) =>
                    setForm({...form, customWorldName: e.target.value})
                  }
                />
              </div>
            )}
            {/* Date, Time, Duration Row */}
            {selectedSession &&
              (selectedSession.status === "completed" ||
                isSessionDone(selectedSession.date)) && (
                <div className="env-session-info-alert">
                  Note: Date and time cannot be modified for completed sessions.
                </div>
              )}
            <div className="env-form-group-horizontal">
              <div className="env-form-field">
                <label htmlFor="session-date">Date</label>
                <input
                  id="session-date"
                  className="env-input"
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({...form, date: e.target.value})}
                  disabled={
                    selectedSession !== null &&
                    (selectedSession.status === "completed" ||
                      isSessionDone(selectedSession.date))
                  }
                />
              </div>
              <div className="env-form-field">
                <label htmlFor="session-time">Time</label>
                <input
                  id="session-time"
                  className="env-input"
                  type="time"
                  value={form.time}
                  onChange={(e) => setForm({...form, time: e.target.value})}
                  disabled={
                    selectedSession !== null &&
                    (selectedSession.status === "completed" ||
                      isSessionDone(selectedSession.date))
                  }
                />
              </div>
              <div className="env-form-field">
                <label htmlFor="session-duration">Duration</label>
                <input
                  id="session-duration"
                  className="env-input"
                  type="text"
                  placeholder="e.g., 4 hours, 3-4 hours"
                  value={form.duration}
                  onChange={(e) => setForm({...form, duration: e.target.value})}
                />
              </div>
            </div>
            {/* Location, Max Players, Level Range Row */}
            <div className="env-form-group-horizontal">
              <div className="env-form-field">
                <label htmlFor="session-location">Location</label>
                <input
                  id="session-location"
                  className="env-input"
                  placeholder="Session location"
                  value={form.location}
                  onChange={(e) => setForm({...form, location: e.target.value})}
                />
              </div>
              <div className="env-form-field">
                <label htmlFor="session-max-players">Max Players</label>
                <input
                  id="session-max-players"
                  className="env-input"
                  type="number"
                  min="1"
                  max="10"
                  value={form.maxPlayers}
                  onChange={(e) =>
                    setForm({...form, maxPlayers: parseInt(e.target.value)})
                  }
                />
              </div>
              <div className="env-form-field-small">
                <label htmlFor="session-min-level">Min Level</label>
                <select
                  id="session-min-level"
                  className="env-input-small"
                  value={form.minLevel}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      minLevel: parseInt(e.target.value),
                    })
                  }
                >
                  {Array.from({length: 20}, (_, i) => i + 1).map((level) => (
                    <option key={level} value={level}>
                      {level}
                    </option>
                  ))}
                </select>
              </div>
              <div className="env-form-field-dash">-</div>
              <div className="env-form-field-small">
                <label htmlFor="session-max-level">Max Level</label>
                <select
                  id="session-max-level"
                  className="env-input-small"
                  value={form.maxLevel}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      maxLevel: parseInt(e.target.value),
                    })
                  }
                >
                  {Array.from({length: 20}, (_, i) => i + 1).map((level) => (
                    <option key={level} value={level}>
                      {level}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="env-form-actions">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  resetForm();
                  setPageState("filled");
                }}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={
                  pageState === "editing"
                    ? handleSaveEditSession
                    : handleCreateSession
                }
                disabled={
                  isSubmitting ||
                  !form.title.trim() ||
                  !form.description.trim() ||
                  !form.date ||
                  !form.location ||
                  (form.titan === "other-worlds" &&
                    !form.customWorldName.trim())
                }
              >
                {isSubmitting
                  ? "Saving..."
                  : pageState === "editing"
                    ? "Save Changes"
                    : "Create Session"}
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (pageState === "adding-notes") {
    return (
      <div className="env-page-container">
        <h2 className="env-page-title">Session Management</h2>
        <section className="env-section">
          <div className="env-section-header">
            <button
              className="btn btn-tertiary"
              onClick={() => setPageState("filled")}
            >
              + Create new session
            </button>
          </div>
          <div className="env-card-medium">
            <div className="env-card-title env-session-edit-header">
              <button
                onClick={() => setPageState("filled")}
                className="btn btn-secondary btn-small"
                aria-label="Back"
              >
                Back
              </button>
              {selectedSession?.sessionLog
                ? "EDIT SESSION NOTES"
                : "ADD SESSION NOTES"}
            </div>
            <div className="env-form-group">
              <label htmlFor="dm-notes">Session Events & Notes</label>
              <RichTextEditor
                value={dmNotes}
                onChange={setDmNotes}
                placeholder="Describe what happened during the session, key events, player actions, and any important notes..."
                minHeight="200px"
                maxHeight="400px"
              />
            </div>
            <div className="env-form-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setPageState("filled")}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSaveNotes}
                disabled={!dmNotes.trim()}
              >
                {selectedSession?.sessionLog ? "Update Notes" : "Save Notes"}
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Main view - filled state
  return (
    <div className="env-page-container">
      <h2 className="env-page-title">Session Management</h2>
      <section className="env-section">
        <div className="env-section-header">
          <button
            className="btn btn-tertiary"
            onClick={() => setPageState("creating")}
          >
            + Create new session
          </button>
        </div>
        <div className="env-item-list">
          {sessions.length === 0 ? (
            <div className="env-empty-state">No sessions created</div>
          ) : (
            sessions.map((session) => (
              <div key={session.id} className="env-card-medium">
                <div className="env-session-header">
                  <h3>{session.name}</h3>
                  <div className="env-session-actions">
                    <Tooltip title="Copy calendar link">
                      <IconButton
                        size="small"
                        onClick={() => handleCopyCalendarLink(session.id)}
                        aria-label="Copy calendar link"
                      >
                        <Share2 size={16} />
                      </IconButton>
                    </Tooltip>
                    {(isDM() || isAdmin()) &&
                      !(
                        session.status === "completed" ||
                        isSessionDone(session.date)
                      ) && (
                        <Tooltip title="Add player to session">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSessionForAdd(session);
                              setShowAddPlayer(true);
                            }}
                            aria-label="Add player to session"
                          >
                            <UserPlus2 size={16} />
                          </IconButton>
                        </Tooltip>
                      )}
                    <button
                      className="btn btn-secondary btn-small"
                      onClick={() => handleEditSession(session)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-destructive btn-small"
                      onClick={() => handleDeleteSession(session.id!)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <div className="env-session-details">
                  <div>
                    <strong>Description:</strong>
                    <div style={{marginTop: "8px"}}>
                      <RichTextRenderer content={session.description} />
                    </div>
                  </div>
                  <p>
                    <strong>Date:</strong>{" "}
                    {session.date.toDate().toLocaleDateString()}
                  </p>
                  <p>
                    <strong>Time:</strong>{" "}
                    {session.date.toDate().toLocaleTimeString()}
                  </p>
                  {session.duration && (
                    <p>
                      <strong>Duration:</strong> {session.duration}
                    </p>
                  )}
                  <p>
                    <strong>Location:</strong> {session.location}
                  </p>
                  <p>
                    <strong>Max Players:</strong> {session.maxPlayers}
                  </p>
                  <p>
                    <strong>Status:</strong>{" "}
                    <span
                      className={`env-session-status ${session.status === "completed" || isSessionDone(session.date) ? "env-session-status-completed" : "env-session-status-pending"}`}
                    >
                      {(() => {
                        if (session.status === "completed" || isSessionDone(session.date)) {
                          return "Completed";
                        } else if (session.status === "upcoming") {
                          return "Players Signed Up";
                        } else if (session.status === "available") {
                          return "Available";
                        } else {
                          return session.status;
                        }
                      })()}
                    </span>
                  </p>
                  <p>
                    <strong>Players:</strong> {session.players.length}/
                    {session.maxPlayers}
                  </p>
                </div>
                <div className="env-session-footer">
                  <div className="env-session-buttons-container">
                    {session.players.length > 0 && (
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleViewCharacterSheets(session)}
                      >
                        View Character Sheets ({session.players.length})
                      </button>
                    )}
                    {(() => {
                      const isCompleted =
                        session.status === "completed" || isSessionDone(session.date);

                      return isCompleted &&
                        session.players.length > 0 &&
                        (isDM() || isAdmin()) ? (
                        <button
                          className="btn btn-secondary"
                          onClick={() => handleViewPlayerLogs(session)}
                        >
                          View Player Logs
                        </button>
                      ) : null;
                    })()}

                    {(() => {
                      const isCompleted =
                        session.status === "completed" || isSessionDone(session.date);

                      return isCompleted && (isDM() || isAdmin()) ? (
                        <button
                          className="btn btn-secondary"
                          onClick={() => handleAddNotes(session)}
                        >
                          {session.sessionLog ? "Edit Notes" : "Add Notes"}
                        </button>
                      ) : null;
                    })()}

                    {/* Claim DM Token Button */}
                    {canClaimDMToken(session) && (
                      <button
                        className="btn btn-primary"
                        onClick={() => handleClaimDMToken(session.id!)}
                        disabled={
                          claimingTokens.has(session.id!) ||
                          claimedTokens.has(session.id!)
                        }
                        style={{
                          backgroundColor: claimedTokens.has(session.id!)
                            ? "#28a745"
                            : undefined,
                          opacity: claimedTokens.has(session.id!)
                            ? 0.8
                            : undefined,
                        }}
                      >
                        {claimedTokens.has(session.id!)
                          ? "✓ Token Claimed"
                          : claimingTokens.has(session.id!)
                            ? "Claiming..."
                            : "🎯 Claim DM Token"}
                      </button>
                    )}

                    {session.status === "available" && (
                      <div className="env-session-available-status">
                        ✓ Available for signups
                      </div>
                    )}
                  </div>
                </div>
                {session.sessionLog && (
                  <>
                    <div className="env-titan-divider" />
                    <div className="env-titan-sessions">
                      <div className="env-titan-sessions-header">
                        Session Notes
                      </div>
                      <div className="env-titan-session-item">
                        <div className="env-titan-session-name">
                          Events & Notes
                        </div>
                        <div className="env-titan-session-details">
                          <RichTextRenderer
                            content={session.sessionLog.events}
                          />
                        </div>
                      </div>
                      {session.sessionLog.items.length > 0 && (
                        <div className="env-titan-session-item">
                          <div className="env-titan-session-name">
                            Items Awarded
                          </div>
                          <div className="env-titan-session-details">
                            {session.sessionLog!.items.map((item, index) => (
                              <span key={index}>
                                {item.name} (x{item.amount}) -{" "}
                                {item.description}
                                {index < session.sessionLog!.items.length - 1 &&
                                  " • "}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      </section>

      <Snackbar
        open={copiedOpen}
        autoHideDuration={2500}
        onClose={() => setCopiedOpen(false)}
        anchorOrigin={{vertical: "bottom", horizontal: "center"}}
      >
        <Alert severity="success" onClose={() => setCopiedOpen(false)}>
          Link copied to clipboard
        </Alert>
      </Snackbar>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        message="Are you sure you want to delete this session? This action cannot be undone."
      />

      {selectedSessionForSheets && (
        <CharacterSheetViewer
          sessionId={selectedSessionForSheets.id!}
          sessionName={selectedSessionForSheets.name}
          isOpen={showCharacterSheets}
          onClose={handleCloseCharacterSheets}
        />
      )}

      {sessionForAdd && (
        <DMAddPlayer
          isOpen={showAddPlayer}
          onClose={() => {
            setShowAddPlayer(false);
            setSessionForAdd(null);
          }}
          session={sessionForAdd}
        />
      )}

      <Dialog
        open={showPlayerLogs}
        onClose={handleClosePlayerLogs}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Typography variant="h5" component="h2">
            Player Logs - {selectedSessionForLogs?.name}
          </Typography>
          <IconButton onClick={handleClosePlayerLogs} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{pt: 1}}>
          {sessionPlayerLogs.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 4,
                color: "text.secondary",
              }}
            >
              <Typography variant="h6" gutterBottom>
                No Player Logs Available
              </Typography>
              <Typography variant="body2">
                No player logs are available for this session.
              </Typography>
            </Box>
          ) : (
            <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
              {sessionPlayerLogs.map((log, index) => (
                <Box
                  key={index}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 2,
                    backgroundColor: "background.paper",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 1,
                    }}
                  >
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {log.playerName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {log.characterName} ({log.characterClass}{" "}
                        {log.characterLevel})
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        backgroundColor: log.hasLogged
                          ? "success.light"
                          : "warning.light",
                        color: log.hasLogged
                          ? "success.contrastText"
                          : "warning.contrastText",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                      }}
                    >
                      {log.hasLogged ? "✓ Logged" : "⚠ Not Logged"}
                    </Box>
                  </Box>
                  {log.hasLogged && log.logData && (
                    <Box sx={{mt: 2}}>
                      <Divider sx={{my: 1}} />
                      <Box
                        sx={{display: "flex", flexDirection: "column", gap: 2}}
                      >
                        {/* Session Events */}
                        {log.logData.events && (
                          <Box>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              gutterBottom
                            >
                              Session Events
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                whiteSpace: "pre-wrap",
                                backgroundColor: "grey.50",
                                p: 1.5,
                                borderRadius: 1,
                                border: 1,
                                borderColor: "divider",
                              }}
                            >
                              {log.logData.events}
                            </Typography>
                          </Box>
                        )}

                        {/* Gold Earned */}
                        {log.logData?.currency?.gold && log.logData.currency.gold > 0 && (
                          <Box>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              gutterBottom
                            >
                              Gold Earned
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{fontWeight: 500, color: "success.main"}}
                            >
                              {log.logData.currency.gold} gold pieces
                            </Typography>
                          </Box>
                        )}

                        {/* Items Received */}
                        {log.logData.items && log.logData.items.length > 0 && (
                          <Box>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              gutterBottom
                            >
                              Items Received
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                              }}
                            >
                              {log.logData.items.map((item, itemIndex) => (
                                <Box
                                  key={itemIndex}
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                    p: 1,
                                    backgroundColor: "grey.50",
                                    borderRadius: 1,
                                    border: 1,
                                    borderColor: "divider",
                                  }}
                                >
                                  <Box sx={{flex: 1}}>
                                    <Typography
                                      variant="body2"
                                      sx={{fontWeight: 500}}
                                    >
                                      {item.name}{" "}
                                      {item.quantity > 1 &&
                                        `(x${item.quantity})`}
                                    </Typography>
                                    {item.description && (
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{mt: 0.5}}
                                      >
                                        {item.description}
                                      </Typography>
                                    )}
                                  </Box>
                                  {item.attunement && (
                                    <Box
                                      sx={{
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 0.5,
                                        backgroundColor: "warning.light",
                                        color: "warning.contrastText",
                                        fontSize: "0.7rem",
                                        fontWeight: 500,
                                      }}
                                    >
                                      Attunement
                                    </Box>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}

                        {/* Rewards Summary */}
                        <Box>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            gutterBottom
                          >
                            Rewards Summary
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.5,
                            }}
                          >
                            <Typography variant="body2">
                              <strong>Logged At:</strong>{" "}
                              {formatLoggedAtDate(log.logData.loggedAt)}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Memento Tokens:</strong>{" "}
                              {log.logData.mementoTokensAwarded}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Downtime Days:</strong>{" "}
                              {log.logData.downtimeDaysAwarded}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SessionManagement;
